/** Public e-sign page for Custom Estimates — /estimate-sign/:token (no login).
 *  Shows the customer scope + single price, captures a drawn or typed
 *  signature, and offers a signed-copy PDF download afterwards. */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/estimate`;
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function EstimateSign() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [signing, setSigning] = useState(false);
  const [done, setDone] = useState(null);
  const canvasRef = useRef(null);
  const inkRef = useRef(false);
  const drawingRef = useRef(false);

  useEffect(() => {
    axios.get(`${API}/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setErr("This estimate link is invalid or has been revoked."));
  }, [token]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * dpr;
    c.height = 140 * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#1e3a8a";
  }, [data, done]);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - r.left, y: p.clientY - r.top };
  };
  const start = (e) => { drawingRef.current = true; const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y); ctx.stroke();
    inkRef.current = true;
  };
  const end = () => { drawingRef.current = false; };
  const clearInk = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    inkRef.current = false;
  };

  const sign = async () => {
    if (!name.trim()) return toast.error("Please enter your full name");
    if (!accepted) return toast.error("Please check the acceptance box");
    setSigning(true);
    try {
      const signature_data_url = inkRef.current ? canvasRef.current.toDataURL("image/png") : "";
      const r = await axios.post(`${API}/${token}/sign`, {
        signer_name: name, signer_email: email, accepted: true,
        signature_data_url, signature_font: signature_data_url ? "" : "cursive",
      });
      setDone(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not sign — please try again");
    } finally {
      setSigning(false);
    }
  };

  if (err) return <Shell><div className="text-red-700 font-bold" data-testid="sign-error">{err}</div></Shell>;
  if (!data) return <Shell><div className="text-zinc-400">Loading…</div></Shell>;

  const alreadySigned = data.signed_at || done;
  return (
    <Shell>
      <Toaster richColors position="top-center" />
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-900">SealTech Building Solutions — Project Proposal</div>
      <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950 mt-1" data-testid="sign-title">{data.title}</h1>
      <div className="text-sm text-zinc-500 mt-1">
        {data.customer && <span className="mr-4">Prepared for <b className="text-zinc-800">{data.customer}</b></span>}
        {data.property && <span>{data.property}</span>}
      </div>

      <div className="mt-6 space-y-4">
        {data.sections.map((s, i) => (
          <div key={i} className="border border-zinc-200 rounded-sm bg-white p-4">
            <div className="font-bold text-zinc-950">{s.name}</div>
            {s.scope_notes && <div className="text-sm text-zinc-600 mt-1 whitespace-pre-line">{s.scope_notes}</div>}
            {s.bullets.length > 0 && (
              <ul className="mt-2 space-y-1">
                {s.bullets.map((b, j) => <li key={j} className="text-sm text-zinc-700">•&nbsp;&nbsp;{b}</li>)}
              </ul>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-center justify-between border-2 border-blue-900 rounded-sm bg-blue-50 px-5 py-4">
        <div className="text-[10px] font-bold uppercase tracking-widest text-blue-900">Total Project Investment</div>
        <div className="font-heading text-2xl font-bold text-blue-900 tabular-nums" data-testid="sign-price">{money(data.sell_price)}</div>
      </div>

      {data.notes && <div className="mt-4 text-xs text-zinc-500 whitespace-pre-line">{data.notes}</div>}

      {alreadySigned ? (
        <div className="mt-8 border border-emerald-300 bg-emerald-50 rounded-sm p-5" data-testid="sign-confirmation">
          <div className="font-bold text-emerald-800">✓ Signed{(done?.signed_by_name || data.signed_by_name) ? ` by ${done?.signed_by_name || data.signed_by_name}` : ""}</div>
          <div className="text-sm text-emerald-700 mt-1">Thank you! A signed copy is available below.</div>
          <a href={`${API}/${token}/pdf`} data-testid="download-signed-pdf"
            className="inline-block mt-3 h-10 leading-10 px-5 bg-blue-900 text-white text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-blue-800">
            Download Signed Copy (PDF)
          </a>
        </div>
      ) : (
        <div className="mt-8 border border-zinc-200 rounded-sm bg-white p-5" data-testid="sign-form">
          <div className="font-bold text-zinc-950 mb-3">Accept & Sign</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name *"
              className="h-10 px-3 border border-zinc-300 rounded-sm" data-testid="signer-name" />
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (for your records)"
              className="h-10 px-3 border border-zinc-300 rounded-sm" data-testid="signer-email" />
          </div>
          <div className="mt-3">
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Draw your signature (optional)</label>
              <button onClick={clearInk} className="text-[10px] font-bold uppercase text-zinc-400 hover:text-zinc-950" data-testid="clear-signature">Clear</button>
            </div>
            <canvas ref={canvasRef} style={{ width: "100%", height: 140, touchAction: "none" }}
              className="border border-dashed border-zinc-300 rounded-sm bg-zinc-50"
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={move} onTouchEnd={end} data-testid="signature-canvas" />
          </div>
          <label className="flex items-start gap-2 mt-4 text-sm text-zinc-700 cursor-pointer">
            <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-1" data-testid="accept-checkbox" />
            <span>I accept this proposal and authorize SealTech Building Solutions to proceed with the work described above for {money(data.sell_price)}.</span>
          </label>
          <button onClick={sign} disabled={signing} data-testid="sign-submit-btn"
            className="mt-4 w-full h-11 bg-blue-900 text-white text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-blue-800 disabled:opacity-50">
            {signing ? "Signing…" : "Sign & Accept Proposal"}
          </button>
        </div>
      )}
      <div className="mt-8 text-center text-[10px] text-zinc-400">SealTech Building Solutions · 720-715-9955 · info@sealtechsolutions.co</div>
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-zinc-100 py-8 px-4">
    <div className="max-w-2xl mx-auto">{children}</div>
  </div>
);
